import React from 'react'
import { View } from 'react-native'
import { useNavigation } from '@react-navigation/native'
import { useSelector } from 'react-redux'
import { ActionGoalSummary } from '@src/types'
import { UI_ICONS_MAPPING } from '@src/components/base'
import { StyleService, useStyleSheet } from '@src/style/service'
import { ActionGoalSummaryCard } from '../ActionGoals/components/ActionGoalSummaryCard'
import { endOfCurrentWeek, sortSummariesByCategory, startOfCurrentWeek } from '../ActionGoals/utils'
import {
  useGetActionGoalOptionFromSummary,
  useGetMetricOptionFromSummary,
} from '../ActionGoals/hooks'
import {
  GlucoseKetonesEnum,
  MacrosSectionEnum,
  MeasurementEnum,
  SectionEnum,
} from '../Insights/utils/pageConfig'
import { MIN_ACTION_GOAL_CARDS, SUMMARY_CARD_HEIGHT } from '../ActionGoals/constants'
import { useFetchStats } from '../Insights/utils/hooks'
import { statsSelector } from '../Insights/models/insights.selectors'
import { EmptyActionGoal } from './EmptyActionGoal'

interface ActionGoalSummariesListProps {
  summaries: ActionGoalSummary[]
  dateRange?: { startDate: string; endDate: string }
  showEmptyCards?: boolean
  cardHeight?: number
  allowEditing: boolean
}

export const ActionGoalSummariesList = ({
  summaries,
  dateRange = { startDate: startOfCurrentWeek(), endDate: endOfCurrentWeek() },
  showEmptyCards = true,
  cardHeight = SUMMARY_CARD_HEIGHT,
  allowEditing,
}: ActionGoalSummariesListProps) => {
  const navigation = useNavigation()
  const getActionGoalOptionFromSummary = useGetActionGoalOptionFromSummary()
  const getMetricOptionFromSummary = useGetMetricOptionFromSummary()
  const styles = useStyleSheet(themedStyles)
  const emptyActionGoalCardsCount = Math.max(1, MIN_ACTION_GOAL_CARDS - summaries.length)
  const sortedSummaries = sortSummariesByCategory(summaries)
  const { fetchStats } = useFetchStats()
  const stats = useSelector(statsSelector)
  return (
    <>
      {sortedSummaries.map((summary) => {
        const completions = summary.completions || []
        const iconName = UI_ICONS_MAPPING[summary.actionGoal.icon] || 'check'
        const metricOption = getMetricOptionFromSummary(summary)
        const hasTrend = !!(metricOption?.trendSection && metricOption.statKey)
        const description = summary.actionGoal.summaryDescription || summary.actionGoal.description
        const actionGoalOption = getActionGoalOptionFromSummary(summary)
        if (!actionGoalOption) {
          return null
        }

        const onViewDetailsPress = () => {
          navigation.navigate('ActionGoalForm', {
            mode: allowEditing ? 'edit' : 'view',
            actionGoalOption,
            actionGoal: summary.actionGoal,
            completions,
          })
        }

        const onCardPress = async () => {
          if (hasTrend) {
            const insightStat = stats.find((stat) => stat.meta.key === metricOption.statKey)
            if (!insightStat) {
              await fetchStats({ startDate: dateRange.startDate, endDate: dateRange.endDate })
            }
            navigation.navigate('InsightsDetailedPage', {
              statSection: metricOption.trendSection as SectionEnum,
              statMetaKey: metricOption.statKey as
                | GlucoseKetonesEnum
                | MeasurementEnum
                | MacrosSectionEnum,
              startDate: dateRange.startDate,
              endDate: dateRange.endDate,
            })
          }
        }

        return (
          <View key={summary.id} style={styles.container}>
            <ActionGoalSummaryCard
              onViewDetailsPress={onViewDetailsPress}
              onCardPress={hasTrend ? onCardPress : undefined}
              completions={completions}
              showTrend={hasTrend}
              numberDays={summary.actionGoal.numberDays}
              title={summary.actionGoal.title}
              description={description}
              iconName={iconName}
              cardHeight={cardHeight}
            />
          </View>
        )
      })}
      {showEmptyCards &&
        Array.from({ length: emptyActionGoalCardsCount }).map((_, index) => (
          <View key={index} style={styles.container}>
            <EmptyActionGoal cardHeight={cardHeight} />
          </View>
        ))}
    </>
  )
}

const themedStyles = StyleService.create({
  container: {
    marginBottom: 8,
  },
})
